import React, { useEffect } from "react";
import { Box, Button, Typography, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";

const PaymentContainer = styled(Box)({
  backgroundColor: "#ffffff",
  width: "100%",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
});

const BackgroundOverlay = styled(Box)({
  position: "absolute",
  height: "100%",
  left: 0,
  top: 0,
  width: "100%",
  backgroundImage: `
    radial-gradient(ellipse 42.02px 100.66px at 298.8px 182.49px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 77.28px 8.9043px at 1471.2px 533.38px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 1.62px 84.635px at 682.2px 1290.7px, rgba(197,255,225,0.4) 0%, rgba(255,255,255,0) 100%)
  `,
});

const GradientBackground = styled(Box)({
  position: "absolute",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0) 0%, #e9fbfb 69.712%, rgba(255,255,255,0) 100%)",
  height: "762px",
  left: "1px",
  top: 0,
  width: "100%",
  pointerEvents: "none",
});

const ContentContainer = styled(Box)({
  backgroundColor: "#f4feff",
  border: "1px solid #5bbfd6",
  borderRadius: "16px",
  padding: "48px",
  width: "585px",
  position: "relative",
  boxShadow: "0px 4px 24px 0px rgba(191,229,236,0.25)",
  zIndex: 1,
});

const CloseButton = styled(Button)({
  position: "absolute",
  right: "47px",
  top: "47px",
  minWidth: "28px",
  width: "28px",
  height: "28px",
  padding: 0,
  color: "#033843",
  "&:hover": {
    backgroundColor: "rgba(3, 56, 67, 0.04)",
  },
});

const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
  width: "100%",
  maxWidth: "489px",
  margin: "0 auto",
});

const IconContainer = styled(Box)({
  width: "101px",
  height: "101px",
  borderRadius: "50%",
  backgroundColor: "#c50f1f",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "8px",
});

const ErrorIcon = styled("svg")({
  width: "48px",
  height: "48px",
  fill: "#ffffff",
});

const Title = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#c50f1f",
  textAlign: "center",
  marginBottom: "8px",
});

const Description = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "1.5",
  color: "#033843",
  textAlign: "center",
  maxWidth: "100%",
  marginBottom: "24px",
});

const ActionButton = styled(Button)({
  background: "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)",
  border: "1px solid #ffffff",
  borderRadius: "60px",
  height: "60px",
  width: "253px",
  padding: "12px 24px",
  boxShadow:
    "0px 4px 24px 0px rgba(10,130,157,0.2), 0px 4px 7.3px 0px rgba(133,233,255,0.4)",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "22px",
  color: "#ffffff",
  "&:hover": {
    background: "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)",
  },
});

export default function PaymentFailure() {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleRetryPayment = () => {
    // Navigate back to pricing page to retry payment
    navigate("/dashboard/facilities", {
      state: { currentView: "pricing" },
    });
  };

  const handleClose = () => {
    navigate("/dashboard/facilities", {
      state: { currentView: "facilityValidation", fromPaymentFailure: true },
    });
  };

  return (
    <PaymentContainer>
      <BackgroundOverlay />
      <GradientBackground />

      <ContentContainer>
        <CloseButton onClick={handleClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
          >
            <path
              d="M14 0C6.268 0 0 6.268 0 14s6.268 14 14 14 14-6.268 14-14S21.732 0 14 0zm0 25.2C7.745 25.2 2.8 20.255 2.8 14S7.745 2.8 14 2.8 25.2 7.745 25.2 14 20.255 25.2 14 25.2zM19.449 8.551a.933.933 0 0 0-1.318 0L14 12.682 9.869 8.551a.933.933 0 1 0-1.318 1.318L12.682 14l-4.131 4.131a.933.933 0 1 0 1.318 1.318L14 15.318l4.131 4.131a.933.933 0 1 0 1.318-1.318L15.318 14l4.131-4.131a.933.933 0 0 0 0-1.318z"
              fill="#033843"
            />
          </svg>
        </CloseButton>

        <ContentWrapper>
          <IconContainer>
            <ErrorIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
            </ErrorIcon>
          </IconContainer>

          <Title>Payment Failed</Title>

          <Description>
            Your Payment just failed due to an unforeseen error
          </Description>

          <ActionButton onClick={handleRetryPayment}>
            Retry Payment
          </ActionButton>
        </ContentWrapper>
      </ContentContainer>
    </PaymentContainer>
  );
}
